class AudioService {
  private audioElement: HTMLAudioElement | null = null;
  private isPlaying = false;
  private isPaused = false;
  private audioQueue: Blob[] = [];
  private isProcessingQueue = false;
  private onPlaybackEnd: (() => void) | null = null;

  constructor() {
    // Create audio element
    this.audioElement = new Audio();
    this.audioElement.onended = () => {
      this.isPlaying = false;
      this.isPaused = false;
      console.log('🔇 Audio finished playing');
      if (this.onPlaybackEnd) {
        this.onPlaybackEnd();
        this.onPlaybackEnd = null;
      }
    };
  }

  async playAudio(audioBlob: Blob, onEnd?: () => void): Promise<void> {
    console.log(`🎵 playAudio called with blob size: ${audioBlob.size} bytes, type: ${audioBlob.type}`);

    // Store callback
    if (onEnd) {
      this.onPlaybackEnd = onEnd;
    }

    // Add to queue
    this.audioQueue.push(audioBlob);
    console.log(`🎵 Audio added to queue. Queue size: ${this.audioQueue.length}`);

    // Process queue if not already processing
    if (!this.isProcessingQueue) {
      this.processQueue();
    }
  }

  private async processQueue(): Promise<void> {
    if (this.isProcessingQueue || this.audioQueue.length === 0) {
      return;
    }

    this.isProcessingQueue = true;

    while (this.audioQueue.length > 0) {
      const audioBlob = this.audioQueue.shift();
      if (audioBlob) {
        await this.playAudioImmediate(audioBlob);
      }
    }

    this.isProcessingQueue = false;
  }

  private async playAudioImmediate(audioBlob: Blob): Promise<void> {
    if (!this.audioElement) {
      console.warn('⚠️ Audio element not available');
      return;
    }

    try {
      console.log(`🔊 Starting playback`);

      // Create object URL from blob
      const audioUrl = URL.createObjectURL(audioBlob);
      this.audioElement.src = audioUrl;

      this.isPlaying = true;
      this.isPaused = false;

      // Play and wait for completion
      await this.audioElement.play();
      console.log('▶️ Audio playback started');

      // Wait for audio to finish
      await new Promise<void>((resolve) => {
        const handleEnded = () => {
          URL.revokeObjectURL(audioUrl);
          this.audioElement?.removeEventListener('ended', handleEnded);
          resolve();
        };
        this.audioElement?.addEventListener('ended', handleEnded);
      });
    } catch (error) {
      console.error('❌ Error playing audio:', error);
      if (error instanceof Error) {
        console.error('Error details:', error.message, error.stack);
      }
      this.isPlaying = false;
      this.isPaused = false;
    }
  }

  pause(): void {
    if (this.audioElement && this.isPlaying && !this.isPaused) {
      this.audioElement.pause();
      this.isPaused = true;
      console.log('⏸️ Audio paused');
    }
  }

  resume(): void {
    if (this.audioElement && this.isPaused) {
      this.audioElement.play();
      this.isPaused = false;
      console.log('▶️ Audio resumed');
    }
  }

  getPlayingState(): boolean {
    return this.isPlaying && !this.isPaused;
  }

  getPausedState(): boolean {
    return this.isPaused;
  }

  stop(): void {
    console.log('⏹️ Stopping audio playback');

    // Stop current playing audio
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.currentTime = 0;
      this.audioElement.src = '';
    }

    // Clear the queue
    this.audioQueue = [];
    this.isPlaying = false;
    this.isPaused = false;
    this.isProcessingQueue = false;
    this.onPlaybackEnd = null;
  }

  destroy(): void {
    this.stop();
    this.audioElement = null;
  }
}

// Global singleton instance
export const audioService = new AudioService();
